/**
 * The one place this app talks to the API.
 *
 * Everything the server decided about how it must be called lives here rather than at each call
 * site: the Bearer token, the company header, the refusal shapes, and the paging envelope.
 */

/** What a paged list answers with. Never a bare array — see `nextCursor`. */
export interface Page<T> {
  items: T[];
  /**
   * Pass back as `?after=`. `null` means this was the last page.
   *
   * A short page does NOT mean the end, and a cursor is not a row id: it is
   * `<row key>.<listing fingerprint>`, and replaying one under a different filter is refused with
   * 400 rather than answered with a silently truncated page. So a filter change must clear it.
   */
  nextCursor: string | null;
  /**
   * How many rows the listing holds, counted no further than 1,000 — DS-10b.
   *
   * PRESENT ONLY on the first page of a route that carries `@Counted()`, asked with `?total=1`. Every
   * other page, and every route that does not count, leaves it out: absent means *not counted*, never
   * *none*. A page fetched with `after` never carries it, so keep the first page's.
   */
  total?: number;
  /** True when the listing holds more than 1,000 rows: `total` then reads *"1,000 or more"*. */
  totalCapped?: boolean;
}

export interface ApiErrorBody {
  message?: string | string[];
  error?: string;
  statusCode?: number;
}

/** A refusal from the API, with the server's own words kept intact. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** The server's message. An array when the ValidationPipe refused several fields at once. */
    readonly detail: string,
    readonly body: ApiErrorBody,
    /**
     * DS-6a: the id of the request that failed, from the server's `X-Request-Id` — shown to the person as
     * `Reference: …` so the failure can be found in the server's log and the audit trail. Null when no answer came.
     */
    readonly requestId: string | null = null,
  ) {
    super(detail);
    this.name = 'ApiError';
  }

  /** A cursor that no longer belongs to this listing, or names nothing. Recoverable: drop it. */
  get isStaleCursor(): boolean {
    return this.status === 400 && /cursor/i.test(this.detail);
  }

  /**
   * Somebody else saved this document while it was open.
   *
   * A 409 from an edit route, and the ONLY correct response is to re-read: these routes replace the
   * whole document, so retrying with the same body would discard whatever the other edit did — which
   * is exactly what the version token exists to prevent. So the UI must never offer "try again",
   * only "reload and re-apply".
   *
   * Every other 409 an edit can raise — a posted document, a posting in flight — is not stale and
   * not recoverable by reloading, which is why this reads the server's sentence rather than the
   * status alone.
   */
  get isStaleVersion(): boolean {
    return this.status === 409 && /has changed since you read it|changed while it was being saved/i.test(this.detail);
  }

  /**
   * The server never answered — it is down, restarting, or the network is gone.
   *
   * A category the UI has to keep separate from a REFUSAL, and an editing screen is where the
   * difference costs something: a refusal means the request was understood and rejected, so the form
   * shows what to fix; this means nothing was decided, the document is untouched, and the right
   * advice is "your changes are still here, try again in a moment". Telling somebody their save
   * failed for an unstated reason, when what actually happened is that a reverse proxy could not
   * reach the app, invites them to start retyping.
   *
   * `status === 0` is this class's own marker for a `fetch` that never got a response. The 5xx gate
   * codes are the ones a proxy emits about the upstream rather than about the request.
   */
  get isUnreachable(): boolean {
    return this.status === 0 || this.status === 502 || this.status === 503 || this.status === 504;
  }

  /**
   * The account has to change its password before anything else works.
   *
   * Detected from the refusal as well as from the login response, because a session stored before
   * a staff reset carries a token minted when the flag was clear — the 403 is the only signal then.
   */
  get needsPasswordChange(): boolean {
    return this.status === 403 && /change your password/i.test(this.detail);
  }
}

function messageOf(body: ApiErrorBody, status: number): string {
  const m = body?.message;
  if (Array.isArray(m)) return m.join('. ');
  if (typeof m === 'string' && m) return m;
  if (typeof body?.error === 'string' && body.error) return body.error;
  return `Request failed (${status}).`;
}

export interface Session {
  accessToken: string;
  refreshToken: string;
  companyId: string | null;
  /**
   * Set by the seed for every new account, and by a staff password reset.
   *
   * Carried in the session rather than re-derived, because the API enforces it by REFUSING
   * everything: `JwtAuthGuard` answers 403 on any route not marked `@AllowPasswordChange`. A UI
   * that did not know would show an app-shaped screen where every request failed.
   */
  mustChangePassword?: boolean;
}

type SessionReader = () => Session | null;
type SessionWriter = (next: Session | null) => void;

let readSession: SessionReader = () => null;
let writeSession: SessionWriter = () => {};

/** Wired once at startup by the session store, so this module holds no state of its own. */
export function bindSession(read: SessionReader, write: SessionWriter): void {
  readSession = read;
  writeSession = write;
}

const BASE = '/api/v1';

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  /** Public routes — login, refresh — must not send a company or a stale token. */
  anonymous?: boolean;
  signal?: AbortSignal;
  /**
   * Read the body as TEXT rather than JSON.
   *
   * For the one family of routes that does not return JSON: the statement print views, which are
   * `text/html`. It is an option on `send` rather than a second fetch helper because everything
   * around the parse has to be identical — above all the 401 refresh, since a print link that died
   * on an expired access token would fail precisely for the person who left the tab open longest.
   */
  raw?: boolean;
}

function url(path: string, query?: RequestOptions['query']): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    // An absent filter is ABSENT, never `?status=`. The API treats an empty string as unset too,
    // but sending it would change the listing FINGERPRINT and invalidate the cursor.
    if (v === undefined || v === null || v === '') continue;
    search.set(k, String(v));
  }
  const qs = search.toString();
  return `${BASE}${path}${qs ? `?${qs}` : ''}`;
}

async function send<T>(path: string, options: RequestOptions, retryOn401 = true): Promise<T> {
  const session = options.anonymous ? null : readSession();
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (session?.accessToken) headers.Authorization = `Bearer ${session.accessToken}`;
  // Every route that is not public or installation-level refuses without this, with a 400 that
  // says so. Sending it whenever a company is chosen is simpler than knowing which routes are which.
  if (session?.companyId) headers['X-Company-Id'] = session.companyId;

  let res: Response;
  try {
    res = await fetch(url(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (e) {
    // `fetch` rejects only when no response was received at all — offline, DNS, a refused
    // connection, or an abort. Everything that reached the server, including a 500, resolves.
    //
    // An abort is re-thrown untouched: it is the CALLER cancelling, and a screen that rendered
    // "could not reach the server" every time a user navigated away mid-request would be reporting
    // its own tidying-up as a fault.
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError(0, 'Could not reach the server. Nothing was sent, so nothing has changed.', {});
  }

  if (res.status === 401 && retryOn401 && session?.refreshToken && !options.anonymous) {
    // The access token is short-lived and the refresh token ROTATES on use. One attempt only: a
    // loop here would spend a stolen-token alarm on our own retry, since presenting a rotated
    // refresh token twice is what the server treats as theft and revokes everything for.
    const refreshed = await refresh(session.refreshToken).catch(() => null);
    if (!refreshed) {
      writeSession(null);
      throw new ApiError(401, 'Your session has expired. Sign in again.', {});
    }
    writeSession({ ...refreshed, companyId: session.companyId });
    return send<T>(path, options, false);
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorBody;
    throw new ApiError(res.status, messageOf(body, res.status), body, res.headers.get('x-request-id'));
  }
  if (res.status === 204) return undefined as T;
  if (options.raw) return (await res.text()) as T;
  return (await res.json()) as T;
}

export interface LoginResult {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: { id: string; email: string; fullName: string; mustChangePassword: boolean };
}

/** Change your own password. Returns a FRESH token pair — the server revokes every other session. */
export function changePassword(currentPassword: string, newPassword: string): Promise<LoginResult> {
  return send<LoginResult>('/auth/change-password', {
    method: 'POST',
    body: { currentPassword, newPassword },
  });
}

export function login(email: string, password: string, totpCode?: string): Promise<LoginResult> {
  return send<LoginResult>('/auth/login', {
    method: 'POST',
    body: { email, password, ...(totpCode ? { totpCode } : {}) },
    anonymous: true,
  });
}

function refresh(refreshToken: string): Promise<LoginResult> {
  return send<LoginResult>('/auth/refresh', {
    method: 'POST',
    body: { refreshToken },
    anonymous: true,
  });
}

export interface Membership {
  id: string;
  name: string;
  baseCurrency: string;
  timezone: string;
  /** The membership baseline — `admin` or `member`. Says who may administer, not what they do. */
  role: string;
  /** Every role held here today, baseline first. This is the one to SHOW; `role` is a capability. */
  roles: string[];
  isDefault: boolean;
}

export const api = {
  memberships: () => send<Membership[]>('/me/memberships', {}),
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    send<T>(path, { query, signal }),
  /**
   * A route that answers with HTML rather than JSON — today, only the statement print views.
   *
   * It exists because those routes cannot be reached by an ordinary link. Authentication here is a
   * BEARER HEADER, and a browser navigating to a new tab sends cookies, not headers — so
   * `<a href="…/print" target="_blank">` is a guaranteed 401. The caller fetches the HTML with a
   * token like any other request and opens the result itself.
   */
  text: (path: string, query?: RequestOptions['query']) => send<string>(path, { query, raw: true }),
  post: <T>(path: string, body?: unknown) => send<T>(path, { method: 'POST', body }),
  /**
   * PUT, which in this API always means REPLACE THE WHOLE DOCUMENT.
   *
   * Not PATCH, and the server's own reason is worth repeating at the call site: `line_no` is
   * positional and no DTO carries a line identity, so a merge would need ids no client has ever
   * seen. Anything omitted from the body is omitted from the document.
   */
  put: <T>(path: string, body: unknown) => send<T>(path, { method: 'PUT', body }),
  /**
   * PATCH, which in this API always means CHANGE ONLY WHAT IS SENT.
   *
   * The opposite of `put` above, and the distinction is real rather than stylistic: PUT replaces a
   * whole document because line numbers are positional and no DTO carries a line identity, while
   * PATCH is used where the body is a flat set of independent fields — the company record, a
   * membership role — and omitting one must leave it alone.
   */
  patch: <T>(path: string, body: unknown) => send<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => send<T>(path, { method: 'DELETE' }),
  logout: (refreshToken: string) =>
    send<unknown>('/auth/logout', { method: 'POST', body: { refreshToken }, anonymous: true }),
};
